// One explicit entry check; no timers, sockets or foreground/network listeners.
const pendingLoads = new WeakMap()
const REVISION = /^[a-f0-9]{64}$/

export function hubCacheIdentity() {
  try {
    const owner = String(JSON.parse(localStorage.getItem("user") || "null")?.user?._id || "")
    const storedToken = localStorage.getItem("token") || ""
    const token = ["null", "undefined"].includes(storedToken) ? "" : storedToken
    const storedRights = JSON.parse(localStorage.getItem("userRights") || "null")?.userRights
    const rights = Array.isArray(storedRights) ? [...storedRights].sort() : []
    return { owner, token, scope: owner && token ? JSON.stringify([owner, rights]) : "" }
  } catch (_) {
    return { owner: "", token: "", scope: "" }
  }
}

function validServerState(value, owner) {
  return value && value.ownerId === owner && typeof value.revision === "string" && REVISION.test(value.revision)
}

export function prepareHubCache(state, identity) {
  if (state.hubCacheScope === identity.scope && identity.scope) return
  state.hubWorkoutPlan = null
  state.hubGroupedSessions = []
  state.hubGroupedExercises = []
  state.oldWorkoutPlans = []
  state.hubCacheScope = identity.scope
  state.hubCacheRevision = null
  state.hubCacheReady = false
  // Never clear activeSplit/liveSession/completedSetsMap here.
}

export function syncWorkoutHub({ state, commit }, api, { freshCheck = false, identity = hubCacheIdentity } = {}) {
  const account = identity()
  if (!account.scope || state.hubCacheScope !== account.scope) commit("PREPARE_HUB_CACHE", account)
  if (!account.owner || !account.token) return Promise.reject(new Error("Keine Anmeldung vorhanden."))
  const previous = pendingLoads.get(state)
  if (!freshCheck && previous && previous.account.scope === account.scope && previous.account.token === account.token) return previous.promise
  const request = { account, promise: null }
  pendingLoads.set(state, request)
  const isCurrent = () => {
    const current = identity()
    return pendingLoads.get(state) === request && current.scope === account.scope && current.token === account.token
  }
  request.promise = (async () => {
    const before = await api.fetchWorkoutHubState().catch(error => {
      // During a staggered deployment the old server can still serve the
      // original full response. Never mark that response as revision-validated.
      if (error?.response?.status === 404) return null
      throw error
    })
    if (!isCurrent()) return { changed: false, ignored: true }
    if (before && !validServerState(before, account.owner)) throw new Error("Ungültiger Trainingsstand.")
    if (before && state.hubCacheScope === account.scope && state.hubCacheReady && state.hubCacheRevision === before.revision) return { changed: false }

    const [plan, history] = await Promise.all([
      api.fetchOwnWorkout().catch(error => {
        if (error?.response?.status === 404) return null
        throw error
      }),
      api.fetchExistingSessions(0),
    ])
    if (!isCurrent()) return { changed: false, ignored: true }
    if (!Array.isArray(history?.groupedSessions) || !Array.isArray(history?.groupedExercises)) throw new Error("Unvollständiger Trainingsverlauf.")
    // Only a changed/first load needs this second small check. If a save raced
    // with the snapshot, keep it visible but don't certify it as up to date.
    let after = null
    if (before) {
      try { after = await api.fetchWorkoutHubState() } catch (_) { /* retry on next entry, never in a loop */ }
    }
    if (!isCurrent()) return { changed: false, ignored: true }
    const revision = before && validServerState(after, account.owner) && after.revision === before.revision ? after.revision : null
    commit("MERGE_HUB_DATA", {
      workout: plan?.splits ? plan : null,
      newSessions: history.groupedSessions,
      newExercises: history.groupedExercises,
      isDelta: false,
      cache: { scope: account.scope, revision },
    })
    return { changed: true }
  })().finally(() => {
    if (pendingLoads.get(state) === request) pendingLoads.delete(state)
  })
  return request.promise
}
